import React, { useEffect, useMemo, useRef, useState } from "react";
import iziToast from "izitoast";
import { Percent, Plus, Search, Trash2, Upload, Landmark, Save } from "lucide-react";
import Spinner from "components/Spinner";
import { Field, SavedChip, SectionCard, inputCls } from "components/profile/ProfileUi";
import { getActiveDistributor } from "actions/distributorActions";
import {
  deleteHsnRateAPI,
  getQuotationSettingsAPI,
  saveHsnRatesAPI,
  saveQuotationSettingsAPI,
} from "actions/quotationActions";
import { normalizeHsn } from "utils/quotationUtils";

// Shown even before a distributor is connected, so margins can be set up front.
const COMMON_DISTRIBUTORS = ["Digikey", "Mouser", "Element14", "TME"];

const toast = (ok, message) =>
  iziToast[ok ? "success" : "error"]({
    title: ok ? "Saved" : "Error",
    message,
    position: "topCenter",
  });

const pctInput =
  "h-10 w-full px-3 bg-slate-50/90 border border-slate-200 rounded-xl text-sm text-slate-800 outline-none focus:border-s360-violet focus:ring-4 focus:ring-s360-violet/10 disabled:opacity-60 tabular-nums";

// Read "HSN code, Duty %, Description" rows from an .xlsx or .csv file. Header
// names are matched loosely; without a recognisable header the first three
// columns are used. Duty given as a fraction (0.11) is read as 11%.
const parseHsnFile = async (file) => {
  let rows = [];
  if (/\.csv$/i.test(file.name)) {
    const text = await file.text();
    rows = text
      .split(/\r?\n/)
      .filter((l) => l.trim())
      .map((l) => l.split(",").map((c) => c.replace(/^"|"$/g, "").trim()));
  } else {
    const ExcelJS = (await import("exceljs")).default;
    const wb = new ExcelJS.Workbook();
    await wb.xlsx.load(await file.arrayBuffer());
    const ws = wb.worksheets[0];
    ws?.eachRow((row) => {
      rows.push(
        row.values.slice(1).map((v) => {
          if (v && typeof v === "object") return String(v.result ?? v.text ?? "");
          return v == null ? "" : String(v).trim();
        }),
      );
    });
  }
  if (!rows.length) return [];

  const header = rows[0].map((h) => h.toLowerCase());
  const find = (re) => header.findIndex((h) => re.test(h));
  let hsnCol = find(/hs|hsn/);
  let dutyCol = find(/duty/);
  let descCol = find(/desc/);
  const hasHeader = hsnCol >= 0 && dutyCol >= 0;
  if (!hasHeader) [hsnCol, dutyCol, descCol] = [0, 1, 2];

  const parsed = rows
    .slice(hasHeader ? 1 : 0)
    .map((r) => ({
      hsn_code: normalizeHsn(r[hsnCol]),
      duty_pct: parseFloat(String(r[dutyCol] ?? "").replace("%", "")),
      description: descCol >= 0 ? r[descCol] || null : null,
    }))
    .filter((r) => r.hsn_code.length >= 4 && r.hsn_code.length <= 10 && Number.isFinite(r.duty_pct));

  if (parsed.length && parsed.every((r) => r.duty_pct <= 1)) {
    parsed.forEach((r) => (r.duty_pct = +(r.duty_pct * 100).toFixed(4)));
  }
  return parsed;
};

const QuotationSettings = () => {
  const [loading, setLoading] = useState(true);
  const [canEdit, setCanEdit] = useState(false);
  const [distributors, setDistributors] = useState(COMMON_DISTRIBUTORS);

  const [margin, setMargin] = useState("");
  const [distMargins, setDistMargins] = useState({});
  const [defaultDuty, setDefaultDuty] = useState("");
  const [savingMargins, setSavingMargins] = useState(false);
  const [marginFlash, setMarginFlash] = useState(false);

  const [rates, setRates] = useState([]);
  const [edits, setEdits] = useState({});
  const [query, setQuery] = useState("");
  const [newRow, setNewRow] = useState({ hsn_code: "", duty_pct: "", description: "" });
  const [busyHsn, setBusyHsn] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        const [res, active] = await Promise.all([
          getQuotationSettingsAPI(),
          getActiveDistributor().catch(() => null),
        ]);
        if (ignore) return;
        if (res?.status) {
          const s = res.data.settings || {};
          setMargin(String(s.default_margin_pct ?? ""));
          setDistMargins(
            Object.fromEntries(
              Object.entries(s.distributor_margins || {}).map(([k, v]) => [k, String(v)]),
            ),
          );
          setDefaultDuty(s.default_duty_pct == null ? "" : String(s.default_duty_pct));
          setRates(res.data.hsn_rates || []);
          setCanEdit(!!res.data.can_edit);
          const names = new Set([
            ...COMMON_DISTRIBUTORS,
            ...Object.keys(active?.data && typeof active.data === "object" ? active.data : {}),
            ...Object.keys(s.distributor_margins || {}),
          ]);
          setDistributors([...names]);
        }
      } finally {
        if (!ignore) setLoading(false);
      }
    })();
    return () => {
      ignore = true;
    };
  }, []);

  const saveMargins = async (e) => {
    e.preventDefault();
    if (!canEdit || savingMargins) return;
    setSavingMargins(true);
    try {
      const res = await saveQuotationSettingsAPI({
        default_margin_pct: margin === "" ? 0 : Number(margin),
        distributor_margins: distMargins,
        default_duty_pct: defaultDuty === "" ? null : Number(defaultDuty),
      });
      toast(!!res?.status, res?.status ? "Quotation margins updated" : res?.message || "Couldn't save margins.");
      if (res?.status) {
        setMarginFlash(true);
        setTimeout(() => setMarginFlash(false), 2200);
      }
    } finally {
      setSavingMargins(false);
    }
  };

  const upsertRates = async (list, okMsg) => {
    setBusyHsn(true);
    try {
      const res = await saveHsnRatesAPI(list);
      if (!res?.status) {
        toast(false, res?.message || "Couldn't save the HSN rows.");
        return false;
      }
      const saved = res.data.rates || [];
      setRates((prev) => {
        const byCode = new Map(prev.map((r) => [r.hsn_code, r]));
        saved.forEach((r) => byCode.set(r.hsn_code, r));
        return [...byCode.values()].sort((a, b) => a.hsn_code.localeCompare(b.hsn_code));
      });
      toast(true, okMsg(saved.length));
      return true;
    } finally {
      setBusyHsn(false);
    }
  };

  const addRow = async (e) => {
    e.preventDefault();
    const code = normalizeHsn(newRow.hsn_code);
    if (code.length < 4 || newRow.duty_pct === "") {
      toast(false, "Enter an HSN code (4–10 digits) and a duty %.");
      return;
    }
    const ok = await upsertRates(
      [{ ...newRow, hsn_code: code, duty_pct: Number(newRow.duty_pct) }],
      () => `HSN ${code} saved`,
    );
    if (ok) setNewRow({ hsn_code: "", duty_pct: "", description: "" });
  };

  const saveEdit = async (row) => {
    const e = edits[row.id];
    if (!e) return;
    const ok = await upsertRates(
      [{ hsn_code: row.hsn_code, duty_pct: Number(e.duty_pct ?? row.duty_pct), description: e.description ?? row.description }],
      () => `HSN ${row.hsn_code} updated`,
    );
    if (ok) setEdits(({ [row.id]: _, ...rest }) => rest);
  };

  const removeRow = async (row) => {
    setBusyHsn(true);
    try {
      const res = await deleteHsnRateAPI(row.id);
      if (res?.status) setRates((prev) => prev.filter((r) => r.id !== row.id));
      else toast(false, res?.message || "Couldn't delete the row.");
    } finally {
      setBusyHsn(false);
    }
  };

  const onImport = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      const parsed = await parseHsnFile(file);
      if (!parsed.length) {
        toast(false, "No HSN rows found. Use columns: HSN code, Duty %, Description.");
        return;
      }
      await upsertRates(parsed, (n) => `${n} HSN rows imported`);
    } catch {
      toast(false, "Couldn't read that file. Use .xlsx or .csv.");
    }
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rates;
    return rates.filter(
      (r) => r.hsn_code.includes(normalizeHsn(q) || q) || (r.description || "").toLowerCase().includes(q),
    );
  }, [rates, query]);

  const loadingChip = loading ? (
    <span className="shrink-0 inline-flex items-center gap-1.5 text-xs font-medium text-slate-500">
      <Spinner bare size={16} /> Loading…
    </span>
  ) : null;
  const readOnlyNote = !loading && !canEdit && (
    <div className="mx-5 mt-4 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 sm:mx-6">
      Only the account owner or an admin can change these settings.
    </div>
  );

  return (
    <div className="grid grid-cols-1 gap-6 xl:grid-cols-[0.8fr_1.2fr]">
      <SectionCard
        title="Quotation margins"
        desc="Default markup added on landing value in every quotation"
        icon={Percent}
        onSubmit={saveMargins}
        action={loadingChip || <SavedChip show={marginFlash} />}
      >
        {readOnlyNote}
        <div className="grid grid-cols-1 gap-4 p-5 sm:grid-cols-2 sm:p-6">
          <Field label="Default margin %">
            <input
              type="number" min="0" max="500" step="0.5"
              className={inputCls}
              disabled={!canEdit}
              value={margin}
              onChange={(e) => setMargin(e.target.value)}
              placeholder="e.g. 12"
            />
          </Field>
          <Field label="Default duty %" optional>
            <input
              type="number" min="0" max="100" step="0.5"
              className={inputCls}
              disabled={!canEdit}
              value={defaultDuty}
              onChange={(e) => setDefaultDuty(e.target.value)}
              placeholder="Used when an HSN code isn't listed"
            />
          </Field>
          <div className="sm:col-span-2">
            <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
              Margin by distributor <span className="font-normal normal-case tracking-normal text-slate-400">blank = default</span>
            </div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-2 md:grid-cols-3">
              {distributors.map((d) => (
                <label key={d} className="flex items-center gap-2">
                  <span className="w-20 shrink-0 truncate text-sm text-slate-700" title={d}>{d}</span>
                  <input
                    type="number" min="0" max="500" step="0.5"
                    className={pctInput}
                    disabled={!canEdit}
                    value={distMargins[d] ?? ""}
                    placeholder={margin || "0"}
                    onChange={(e) => setDistMargins((p) => ({ ...p, [d]: e.target.value }))}
                  />
                </label>
              ))}
            </div>
          </div>
        </div>
        <div className="flex justify-end px-5 pb-5 sm:px-6">
          <button
            type="submit"
            disabled={!canEdit || savingMargins || loading}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-s360-violet px-4 py-2.5 text-sm font-semibold text-white shadow-[0_10px_30px_-12px_rgba(95,71,201,0.7)] transition hover:bg-s360-violet-600 disabled:opacity-60"
          >
            <Save className="h-4 w-4" />
            {savingMargins ? "Saving…" : "Save margins"}
          </button>
        </div>
      </SectionCard>

      <SectionCard
        title="Duty by HSN code"
        desc="Import duty % filled into quotations by each part's HSN code"
        icon={Landmark}
        tone="teal"
        action={
          loadingChip ||
          (canEdit && (
            <>
              <input ref={fileRef} type="file" accept=".xlsx,.csv" className="hidden" onChange={onImport} />
              <button
                type="button"
                disabled={busyHsn}
                onClick={() => fileRef.current?.click()}
                title="Columns: HSN code, Duty %, Description"
                className="inline-flex shrink-0 items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
              >
                <Upload className="h-3.5 w-3.5" /> Import Excel/CSV
              </button>
            </>
          ))
        }
      >
        {readOnlyNote}
        <div className="flex flex-col gap-3 p-5 sm:p-6">
          <div className="relative flex items-center">
            <Search className="pointer-events-none absolute left-3 h-4 w-4 text-slate-400" />
            <input
              className={`${inputCls} pl-9`}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={`Search ${rates.length} HSN codes or descriptions`}
            />
          </div>

          <div className="max-h-[360px] overflow-auto rounded-xl border border-slate-200">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-slate-50 text-left text-[11px] uppercase tracking-[0.12em] text-slate-500">
                <tr>
                  <th className="px-3 py-2 font-semibold">HSN code</th>
                  <th className="px-3 py-2 font-semibold w-28">Duty %</th>
                  <th className="px-3 py-2 font-semibold">Description</th>
                  {canEdit && <th className="w-24" />}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {canEdit && (
                  <tr className="bg-[#F7FFFD]">
                    <td className="px-3 py-2">
                      <input
                        className={`${pctInput} font-mono`}
                        value={newRow.hsn_code}
                        onChange={(e) => setNewRow((p) => ({ ...p, hsn_code: e.target.value }))}
                        placeholder="85366940"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="number" min="0" max="100" step="0.5"
                        className={pctInput}
                        value={newRow.duty_pct}
                        onChange={(e) => setNewRow((p) => ({ ...p, duty_pct: e.target.value }))}
                        placeholder="11"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input
                        className={pctInput}
                        value={newRow.description}
                        onChange={(e) => setNewRow((p) => ({ ...p, description: e.target.value }))}
                        placeholder="Optional"
                      />
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button
                        type="button"
                        disabled={busyHsn}
                        onClick={addRow}
                        className="inline-flex items-center gap-1 rounded-lg bg-s360-teal px-2.5 py-1.5 text-xs font-semibold text-slate-900 disabled:opacity-60"
                      >
                        <Plus className="h-3.5 w-3.5" /> Add
                      </button>
                    </td>
                  </tr>
                )}
                {filtered.map((r) => {
                  const e = edits[r.id];
                  const set = (k, v) => setEdits((p) => ({ ...p, [r.id]: { ...p[r.id], [k]: v } }));
                  return (
                    <tr key={r.id}>
                      <td className="px-3 py-2 font-mono text-slate-800">{r.hsn_code}</td>
                      <td className="px-3 py-2">
                        {canEdit ? (
                          <input
                            type="number" min="0" max="100" step="0.5"
                            className={pctInput}
                            value={e?.duty_pct ?? String(Number(r.duty_pct))}
                            onChange={(ev) => set("duty_pct", ev.target.value)}
                          />
                        ) : (
                          <span className="tabular-nums">{Number(r.duty_pct)}%</span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {canEdit ? (
                          <input
                            className={pctInput}
                            value={e?.description ?? r.description ?? ""}
                            onChange={(ev) => set("description", ev.target.value)}
                          />
                        ) : (
                          <span className="text-slate-600">{r.description}</span>
                        )}
                      </td>
                      {canEdit && (
                        <td className="px-3 py-2 text-right whitespace-nowrap">
                          {e && (
                            <button
                              type="button"
                              disabled={busyHsn}
                              onClick={() => saveEdit(r)}
                              className="mr-1 rounded-lg bg-s360-violet px-2 py-1 text-xs font-semibold text-white disabled:opacity-60"
                            >
                              Save
                            </button>
                          )}
                          <button
                            type="button"
                            disabled={busyHsn}
                            onClick={() => removeRow(r)}
                            aria-label={`Delete HSN ${r.hsn_code}`}
                            className="rounded-lg p-1.5 text-slate-400 hover:bg-rose-50 hover:text-rose-600 disabled:opacity-60"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })}
                {!loading && filtered.length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-3 py-6 text-center text-xs text-slate-500">
                      {rates.length ? "No HSN codes match your search." : "No HSN codes yet. Add one above or import a sheet."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <p className="m-0 text-[11px] text-slate-500">
            Codes match on all digits first, then the 6- and 4-digit heading. Duty can still be changed per line in a quotation.
          </p>
        </div>
      </SectionCard>
    </div>
  );
};

export default QuotationSettings;
